import React, { useRef, useEffect, useMemo, useCallback, useState } from "react";
import AgGridReact from "../../../common/AgGrid/AgGridReact.jsx";
import { gridTheme } from "../../../common/AgGrid/gridTheme.js";
import { ClientSideRowModelModule } from "ag-grid-community";
import { useDispatch, useSelector } from "react-redux";
import { buildColumnDefs } from "../../../datagrid/hooks/useDynamicCols.jsx";
import LoadingIndicator from "../../../common/loading_indicator.jsx";
import { cloneDeep } from "lodash";
import styles from './GridWidget.module.scss';
import { applyContentBlockData, updateLayout } from "../../redux/dashboardSlice.js";
import { useWidgetInnerSize } from "../../hooks/useWidgetInnerSize.js";
import { useWidgetAutoHeight } from "../../hooks/useWidgetAutoHeight.js";
import { useDatasource } from '../../hooks/useDatasource.js';
import useRowId from "../../../datagrid/hooks/useRowId.js";
import { selectDatasourceCols, selectDatasourceRows } from "../../redux/datasourceSlice.js";
import GridWidgetV2 from './GridWidgetV2.jsx';
import { selectUsesDatasourceV2 } from '../../datasource_v2/selectors.js';

function Grid({ widgetId, widgetRef, theme, editable, ...rest }) {
  const [_, getRowId] = useRowId();
  const dispatch = useDispatch();
  const userDefinedColumns = useSelector((state) => state.dashboard.widgets[widgetId].settings.columns || []);
  const datasourceId = useSelector(state => state.dashboard.widgets[widgetId]?.data_datasource?.id);
  const filteredRows = useSelector(selectDatasourceRows(datasourceId, widgetId));
  const columns = useSelector(selectDatasourceCols(datasourceId, widgetId));
  const dimensions = useWidgetInnerSize(widgetRef, theme);
  const rows = cloneDeep(filteredRows); // TODO: rows need to be mutatable because bad implementation from validateNumber
  const layout = useSelector((state) => state.dashboard.layout[widgetId]);
  const useContextMenu = useSelector((state) => state.dashboard.widgets[widgetId].settings.useContextMenu);
  const targetWidget = useSelector((state) => state.dashboard.widgets[widgetId].settings.targetWidget);
  const targetWidgetId = targetWidget?.enabled ? targetWidget?.widgetId : null;
  const gridContainerRef = useRef(null);
  const gridRef = useRef(null);
  const { setTargetHeight } = useWidgetAutoHeight({ widgetId, editable });

  useDatasource({ widgetId, live: true });

  useEffect(() => {
    resizeGrid();
  }, [layout]);

  const gridColDefs = useMemo(() => {
    const newColDefs = buildColumnDefs(columns, { editMode: false });
    return userDefinedColumns.map((c) => {

      const datasourceCol = newColDefs.find(
        (dc) => (dc.accessor || dc.field) === (c.accessor || c.field)
      );
      return {
        ...(datasourceCol || {}),
        ...c,
        suppressHeaderContextMenu: !useContextMenu,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true
      };
    });
  }, [userDefinedColumns, columns, useContextMenu]);

  const loadingOverlayComponent = useMemo(() => {
    return LoadingIndicator;
  }, []);

  const setRequiredGridHeight = () => {
    const api = gridRef.current?.api;
    const container = gridContainerRef.current;

    if (!api || !container) {
      setTargetHeight(0);
    };

    const rowHeight = api.getDisplayedRowAtIndex(0)?.rowHeight || 42;
    const rowsHeight = api.getDisplayedRowCount() * rowHeight;

    const headerHeight = container.querySelector('.ag-header')?.getBoundingClientRect().height || 0;
    const currentHeight = container.getBoundingClientRect().height;
    const widgetHeaderHeight = dimensions?.header || 0;

    setTargetHeight(Math.max(rowsHeight + headerHeight + widgetHeaderHeight + 1, currentHeight + widgetHeaderHeight));
  };

  useEffect(() => {
    if (gridRef.current?.api && dimensions?.height > 0) {
      setRequiredGridHeight();
    }
  }, [!dimensions?.height]);

  const resizeGrid = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.sizeColumnsToFit();
    }
  };

  const replaceDataMap = useCallback((params) => {
    const cols = params.api.getColumns();

    return Object.fromEntries(
      cols.map(col => {
        const userDefinedCol = userDefinedColumns.find(c => c.accessor === col.colId);
        if (!userDefinedCol?.contentToReplace) return null;

        const value =
          params.api.getCellValue({
            rowNode: params.node,
            colKey: col,
            useFormatter: true,
          }) ?? '';

        return [userDefinedCol.contentToReplace, value];
      }).filter(Boolean)
    );
  }, [userDefinedColumns]);

  const onRowClicked = (params) => {
    if (!targetWidgetId) return;
    const data = replaceDataMap(params);
    dispatch(applyContentBlockData({ targetWidgetId, data }))
  }

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }} className={styles['grid-widget-container']} {...rest}>
      <div
        ref={gridContainerRef}
        className="grid-theme mb-1 datasource-table"
        style={{
          width: "100%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          height: "100%",
          minHeight: "0px",
        }}
      >
        <AgGridReact theme={gridTheme}
          groupHeaderHeight={20}
          ref={gridRef}
          columnDefs={gridColDefs}
          rowData={rows}
          onGridReady={(params) => {
            params.api.sizeColumnsToFit();
          }}
          onFirstDataRendered={(params) => {
            setRequiredGridHeight();
          }}
          loadingOverlayComponent={loadingOverlayComponent}
          modules={[ClientSideRowModelModule]}
          {...(!useContextMenu ? { getContextMenuItems: () => [] } : {})}
          onRowClicked={onRowClicked}
          getRowId={params => String(params.data.id || params.data._row_id || getRowId(params))}
        />
      </div>
    </div>
  );
}

function GridWidget({ widgetId, ...rest }) {
  const usesDatasourceV2 = useSelector(selectUsesDatasourceV2(widgetId));
  if (usesDatasourceV2) return <GridWidgetV2 widgetId={widgetId} />;

  return <Grid widgetId={widgetId} {...rest} />;
}

export default GridWidget;
